// Iter 100.2 — Monthly Attendance Register (primary Attendance screen)
//
// Calendar-grid view of attendance for an entire month, composing the
// Iter 100.1 read-only endpoint (/api/attendance/report/monthly-register).
//
// Layout (top → bottom):
//   • PageHeader — title, month navigator, "Generate Payroll Run" CTA.
//   • Sub-nav (pill row) — sticky links to the OTHER attendance reports
//     so the Monthly Register acts as the new primary landing while
//     Daily / Monthly-Summary / Anomalies / Regularization / Trends
//     remain one click away (operator directive Iter 100.2 §9).
//   • Org Summary band — 11 tiles: Total Employees / Present / Absent /
//     Leave / Half Day / Attendance % / Present % / Working Hrs /
//     Active Hrs / Idle Hrs / Avg Productivity %.
//   • Filters band — Department / Reporting Manager / Employee / Status.
//   • Register table — sticky Employee + Department columns, 1-N day
//     cells with colour-coded status codes, day-cell click → drill-down
//     popover.
//
// Privacy invariant carried forward — UI never renders any prohibited
// fields (no screenshots / keystrokes / browser URLs / app names / etc).
//
// Read-only — no new write paths. "Generate Payroll Run" deep-links to
// the existing Payroll Runs console with the current period pre-filled.
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    ArrowsClockwise, CalendarBlank, CaretLeft, CaretRight, FunnelSimple,
    Calculator, ChartBar, ShieldCheck, X, Lock, Snowflake, Pulse,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import PageHeader from "../../components/layout/PageHeader";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import { formatTime } from "../../lib/format";

// ─── Constants ────────────────────────────────────────────────────────
const MONTH_NAMES = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
];

const CODE_TONE = {
    P: "bg-emerald-100 text-emerald-800 border-emerald-200",
    A: "bg-rose-100 text-rose-800 border-rose-200",
    HD: "bg-amber-100 text-amber-800 border-amber-200",
    L: "bg-sky-100 text-sky-800 border-sky-200",
    LOP: "bg-orange-100 text-orange-800 border-orange-200",
    WO: "bg-slate-100 text-slate-600 border-slate-200",
    H: "bg-violet-100 text-violet-800 border-violet-200",
    PR: "bg-yellow-100 text-yellow-800 border-yellow-200",
    "-": "bg-white text-slate-300 border-slate-100",
};

const CODE_LABEL = {
    P: "Present", A: "Absent", HD: "Half Day", L: "Paid Leave",
    LOP: "Unpaid Leave (LOP)", WO: "Weekly Off", H: "Holiday",
    PR: "Pending Regularization", "-": "No Snapshot",
};

const FREEZE_TONE = {
    unfrozen: { tone: "bg-slate-100 text-slate-700 border-slate-200",
                icon: CalendarBlank, label: "Unfrozen" },
    frozen:   { tone: "bg-sky-100 text-sky-800 border-sky-200",
                icon: Snowflake, label: "Frozen" },
    locked:   { tone: "bg-violet-100 text-violet-800 border-violet-200",
                icon: Lock, label: "Locked" },
};

// ─── Sub-nav for secondary reports ────────────────────────────────────
const SUB_NAV = [
    { to: "/admin/attendance/reports/monthly-register", label: "Monthly Register", primary: true },
    { to: "/admin/attendance/reports/monthly-summary", label: "Monthly Summary" },
    { to: "/admin/attendance/reports/daily-summary", label: "Daily Summary" },
    { to: "/admin/attendance/reports/anomalies", label: "Anomalies" },
    { to: "/admin/attendance/reports/regularization", label: "Regularization" },
    { to: "/admin/attendance/reports/trends", label: "Trends" },
];

function today() {
    const d = new Date();
    return { year: d.getFullYear(), month: d.getMonth() + 1 };
}

function pctBg(pct) {
    if (pct == null) return "bg-slate-100 text-slate-600";
    if (pct >= 95) return "bg-emerald-100 text-emerald-800";
    if (pct >= 85) return "bg-amber-100 text-amber-800";
    return "bg-rose-100 text-rose-800";
}

// ─── Tile component ───────────────────────────────────────────────────
function Tile({ label, value, suffix = "", tone = "slate", testid }) {
    const tones = {
        slate: "bg-slate-50 border-slate-200 text-slate-900",
        emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
        rose: "bg-rose-50 border-rose-200 text-rose-900",
        amber: "bg-amber-50 border-amber-200 text-amber-900",
        sky: "bg-sky-50 border-sky-200 text-sky-900",
        violet: "bg-violet-50 border-violet-200 text-violet-900",
        indigo: "bg-indigo-50 border-indigo-200 text-indigo-900",
    };
    return (
        <div className={`rounded-md border p-2.5 ${tones[tone]}`} data-testid={testid}>
            <div className="text-[10px] uppercase tracking-wide opacity-70">{label}</div>
            <div className="text-lg font-bold mt-0.5 leading-tight">
                {value}
                {suffix && <span className="text-xs font-normal opacity-70 ml-1">{suffix}</span>}
            </div>
        </div>
    );
}

// ─── Day-cell drill-down modal ────────────────────────────────────────
function DayDetailModal({ cell, employee, onClose }) {
    if (!cell) return null;
    return (
        <div
            className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4"
            onClick={onClose}
            data-testid="att-reg-day-detail-modal"
        >
            <div
                className="bg-white rounded-lg shadow-xl max-w-md w-full p-5"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-start justify-between mb-3">
                    <div>
                        <div className="text-xs uppercase text-slate-500 tracking-wide">{employee.full_name}</div>
                        <div className="text-lg font-bold text-slate-900">{cell.date}</div>
                    </div>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="att-reg-day-detail-close">
                        <X size={18} />
                    </button>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                        <div className="text-xs text-slate-500">Status</div>
                        <Badge className={`${CODE_TONE[cell.code] || "bg-slate-100"} text-xs mt-0.5`}>
                            {cell.code} · {cell.status || CODE_LABEL[cell.code]}
                        </Badge>
                    </div>
                    <div>
                        <div className="text-xs text-slate-500">Source</div>
                        <div className="font-medium">{cell.source || "—"}</div>
                    </div>
                    <div>
                        <div className="text-xs text-slate-500">Check-in</div>
                        <div className="font-medium">{cell.check_in_time ? formatTime(cell.check_in_time) : "—"}</div>
                    </div>
                    <div>
                        <div className="text-xs text-slate-500">Check-out</div>
                        <div className="font-medium">{cell.check_out_time ? formatTime(cell.check_out_time) : "—"}</div>
                    </div>
                    <div>
                        <div className="text-xs text-slate-500">Net Working Minutes</div>
                        <div className="font-medium">{cell.net_working_minutes || 0}</div>
                    </div>
                    <div>
                        <div className="text-xs text-slate-500">Flags</div>
                        <div className="text-xs">
                            {cell.is_late && <Badge className="bg-rose-100 text-rose-800 mr-1">Late</Badge>}
                            {cell.is_early_departure && <Badge className="bg-amber-100 text-amber-800 mr-1">Early</Badge>}
                            {cell.is_regularized && <Badge className="bg-sky-100 text-sky-800 mr-1">Regularised</Badge>}
                            {cell.needs_review && <Badge className="bg-yellow-100 text-yellow-800 mr-1">Needs Review</Badge>}
                            {!cell.is_late && !cell.is_early_departure && !cell.is_regularized && !cell.needs_review && (
                                <span className="text-slate-400">—</span>
                            )}
                        </div>
                    </div>
                </div>
                <div className="text-[11px] text-slate-400 mt-4 pt-3 border-t border-slate-100">
                    Read-only — composed from the daily attendance snapshot. To edit, use the
                    {" "}<Link to={`/admin/attendance/snapshots?employee_id=${employee.employee_id}&date=${cell.date}`} className="text-violet-700 underline">Snapshots console</Link>.
                </div>
            </div>
        </div>
    );
}

// ─── Sub-nav pill row ─────────────────────────────────────────────────
function SubNav({ current }) {
    return (
        <div className="flex flex-wrap gap-1.5 items-center" data-testid="att-reg-subnav">
            {SUB_NAV.map((s) => (
                <Link
                    key={s.to}
                    to={s.to}
                    data-testid={`att-reg-subnav-${s.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className={
                        s.to === current
                            ? "px-2.5 py-1 rounded-full text-xs font-medium bg-violet-100 text-violet-800 border border-violet-200"
                            : "px-2.5 py-1 rounded-full text-xs font-medium bg-white text-slate-700 border border-slate-200 hover:border-violet-300 hover:bg-violet-50"
                    }
                >
                    {s.label}
                </Link>
            ))}
        </div>
    );
}

// ─── Main page ────────────────────────────────────────────────────────
export default function AttendanceMonthlyRegister() {
    const navigate = useNavigate();
    const t0 = today();
    const [year, setYear] = useState(t0.year);
    const [month, setMonth] = useState(t0.month);
    const [department, setDepartment] = useState("");
    const [reportingMgr, setReportingMgr] = useState("");
    const [employeeId, setEmployeeId] = useState("");
    const [statusCode, setStatusCode] = useState("");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [dayDetail, setDayDetail] = useState(null);  // {cell, employee}

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = { year, month };
            if (department) params.department = department;
            if (reportingMgr) params.reporting_manager_employee_id = reportingMgr;
            if (employeeId) params.employee_id = employeeId;
            if (statusCode) params.status = statusCode;
            const { data: payload } = await api.get("/attendance/report/monthly-register", { params });
            setData(payload);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load monthly register"));
            setData(null);
        } finally {
            setLoading(false);
        }
    }, [year, month, department, reportingMgr, employeeId, statusCode]);

    useEffect(() => { load(); }, [load]);

    const goPrev = () => {
        if (month === 1) { setYear(year - 1); setMonth(12); }
        else setMonth(month - 1);
    };
    const goNext = () => {
        if (month === 12) { setYear(year + 1); setMonth(1); }
        else setMonth(month + 1);
    };
    const goCurrent = () => {
        const t = today();
        setYear(t.year);
        setMonth(t.month);
    };

    const clearFilters = () => {
        setDepartment("");
        setReportingMgr("");
        setEmployeeId("");
        setStatusCode("");
    };

    const generatePayrollRun = () => {
        const periodMonth = `${year}-${String(month).padStart(2, "0")}`;
        // Existing Payroll Runs console accepts the period_month query string;
        // it pre-fills the "New run" wizard. We do NOT create the run here —
        // this is a deep-link only, consistent with the operator's "no new
        // write path" instruction.
        navigate(`/admin/payroll/runs?period_month=${periodMonth}`);
    };

    const days_in_month = data?.scope?.days_in_month || 31;
    const dayNumbers = useMemo(
        () => Array.from({ length: days_in_month }, (_, i) => i + 1),
        [days_in_month],
    );
    const employees = data?.employees || [];
    const org = data?.org_summary || {};

    return (
        <div className="px-4 sm:px-6 py-5 max-w-[1920px] mx-auto" data-testid="att-reg-root">
            <PageHeader
                eyebrow="Attendance"
                title="Monthly Attendance Register"
                description={`Calendar-grid view for ${MONTH_NAMES[month - 1]} ${year} · ${employees.length} employee${employees.length === 1 ? "" : "s"} · payroll-parity totals from the freeze engine · All times are displayed in IST.`}
                actions={(
                    <div className="flex gap-2 flex-wrap" data-testid="att-reg-header-actions">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={generatePayrollRun}
                            data-testid="att-reg-payroll-cta"
                            title={`Open Payroll Runs for ${MONTH_NAMES[month - 1]} ${year}`}
                        >
                            <Calculator size={14} className="mr-1.5" />
                            Generate Payroll Run
                        </Button>
                        <Button variant="outline" size="sm" onClick={load} data-testid="att-reg-refresh-btn">
                            <ArrowsClockwise size={14} className="mr-1.5" />
                            Refresh
                        </Button>
                    </div>
                )}
            />

            {/* Sub-nav for secondary reports */}
            <div className="mt-4">
                <SubNav current="/admin/attendance/reports/monthly-register" />
            </div>

            {/* Month navigator */}
            <section className="mt-4 flex items-center gap-2" data-testid="att-reg-month-nav">
                <Button variant="outline" size="sm" onClick={goPrev} data-testid="att-reg-prev-month-btn">
                    <CaretLeft size={14} className="mr-1" /> Prev
                </Button>
                <div
                    className="px-3 py-1.5 rounded-md bg-violet-100 text-violet-900 font-bold text-sm border border-violet-200"
                    data-testid="att-reg-month-label"
                >
                    {MONTH_NAMES[month - 1]} {year}
                </div>
                <Button variant="outline" size="sm" onClick={goCurrent} data-testid="att-reg-current-month-btn">
                    Current
                </Button>
                <Button variant="outline" size="sm" onClick={goNext} data-testid="att-reg-next-month-btn">
                    Next <CaretRight size={14} className="ml-1" />
                </Button>
            </section>

            {/* Org Summary tiles */}
            <section className="mt-4 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2" data-testid="att-reg-org-summary">
                <Tile label="Total Employees" value={org.total_employees ?? 0} tone="indigo" testid="att-reg-tile-total-emp" />
                <Tile label="Present Days" value={org.present_days ?? 0} tone="emerald" testid="att-reg-tile-present" />
                <Tile label="Absent Days" value={org.absent_days ?? 0} tone="rose" testid="att-reg-tile-absent" />
                <Tile label="Leave Days" value={org.leave_days ?? 0} tone="sky" testid="att-reg-tile-leave" />
                <Tile label="Half Days" value={org.half_days ?? 0} tone="amber" testid="att-reg-tile-half" />
                <Tile label="Attendance %" value={(org.attendance_pct ?? 0).toFixed(1)} suffix="%" tone="violet" testid="att-reg-tile-att-pct" />
                <Tile label="Present %" value={(org.present_pct ?? 0).toFixed(1)} suffix="%" tone="emerald" testid="att-reg-tile-present-pct" />
                <Tile label="Agent Tracked Hours" value={(org.total_working_hours ?? 0).toFixed(1)} suffix="h" tone="slate" testid="att-reg-tile-agent-tracked" />
                <Tile label="Active Hours" value={(org.total_active_hours ?? 0).toFixed(1)} suffix="h" tone="emerald" testid="att-reg-tile-active" />
                <Tile label="Idle Hours" value={(org.total_idle_hours ?? 0).toFixed(1)} suffix="h" tone="amber" testid="att-reg-tile-idle" />
                <Tile label="Locked Hours" value={(org.total_locked_hours ?? 0).toFixed(1)} suffix="h" tone="violet" testid="att-reg-tile-locked" />
                <Tile label="Avg Productivity" value={org.avg_productivity_pct != null ? org.avg_productivity_pct.toFixed(1) : "—"} suffix={org.avg_productivity_pct != null ? "%" : ""} tone="indigo" testid="att-reg-tile-avg-prod" />
            </section>

            {/* Filters band */}
            <section className="mt-4 bg-white rounded-xl border border-slate-200 p-3" data-testid="att-reg-filters">
                <div className="flex items-center gap-2 mb-2">
                    <FunnelSimple size={14} className="text-slate-500" />
                    <div className="text-xs font-medium text-slate-700">Filters</div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-5 gap-2">
                    <div>
                        <Label htmlFor="dept" className="text-[11px]">Department</Label>
                        <Input id="dept" value={department} onChange={(e) => setDepartment(e.target.value)} placeholder="e.g. Engineering" data-testid="att-reg-filter-dept" />
                    </div>
                    <div>
                        <Label htmlFor="mgr" className="text-[11px]">Reporting Manager (EMP-ID)</Label>
                        <Input id="mgr" value={reportingMgr} onChange={(e) => setReportingMgr(e.target.value)} placeholder="EMP-..." data-testid="att-reg-filter-mgr" />
                    </div>
                    <div>
                        <Label htmlFor="emp" className="text-[11px]">Employee (EMP-ID)</Label>
                        <Input id="emp" value={employeeId} onChange={(e) => setEmployeeId(e.target.value)} placeholder="EMP-..." data-testid="att-reg-filter-emp" />
                    </div>
                    <div>
                        <Label htmlFor="status" className="text-[11px]">Status Code</Label>
                        <select
                            id="status"
                            value={statusCode}
                            onChange={(e) => setStatusCode(e.target.value)}
                            className="w-full h-9 rounded-md border border-slate-300 px-2 text-sm bg-white"
                            data-testid="att-reg-filter-status"
                        >
                            <option value="">All</option>
                            {["P", "A", "L", "HD", "WO", "H", "LOP", "PR"].map((c) => (
                                <option key={c} value={c}>{c} — {CODE_LABEL[c]}</option>
                            ))}
                        </select>
                    </div>
                    <div className="flex items-end">
                        <Button variant="outline" size="sm" onClick={clearFilters} data-testid="att-reg-filter-clear" className="w-full">
                            Clear
                        </Button>
                    </div>
                </div>
            </section>

            {/* Code legend */}
            <section className="mt-3 flex flex-wrap items-center gap-1.5 text-[10px]" data-testid="att-reg-legend">
                <span className="text-slate-500 uppercase tracking-wide font-medium mr-1">Legend:</span>
                {Object.entries(CODE_LABEL).map(([code, label]) => (
                    <Badge key={code} className={`${CODE_TONE[code]} border text-[10px]`}>
                        {code} · {label}
                    </Badge>
                ))}
            </section>

            {/* Register table */}
            {loading ? (
                <div className="mt-4 p-10 text-center text-slate-500" data-testid="att-reg-loading">Loading register…</div>
            ) : !data || employees.length === 0 ? (
                <div className="mt-4 p-10 text-center text-slate-500 rounded-xl border border-dashed border-slate-200" data-testid="att-reg-empty">
                    No employees match the current filters.
                </div>
            ) : (
                <section className="mt-4 bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="att-reg-grid">
                    <div className="overflow-x-auto">
                        <table className="text-xs border-collapse">
                            <thead className="bg-slate-50 text-slate-600">
                                <tr>
                                    <th className="sticky left-0 z-20 bg-slate-50 px-3 py-2 text-left font-medium border-b border-slate-200 min-w-[200px]">Employee</th>
                                    <th className="sticky left-[200px] z-20 bg-slate-50 px-3 py-2 text-left font-medium border-b border-slate-200 min-w-[140px]">Department</th>
                                    <th className="px-2 py-2 text-left font-medium border-b border-slate-200 whitespace-nowrap">Freeze</th>
                                    {dayNumbers.map((d) => (
                                        <th key={d} className="px-1 py-2 text-center font-medium border-b border-slate-200 min-w-[28px]">
                                            {d}
                                        </th>
                                    ))}
                                    <th className="px-2 py-2 text-right font-medium border-b border-l border-slate-300 bg-slate-100 min-w-[42px]" title="Present">P</th>
                                    <th className="px-2 py-2 text-right font-medium border-b border-slate-200 bg-slate-100 min-w-[42px]" title="Absent">A</th>
                                    <th className="px-2 py-2 text-right font-medium border-b border-slate-200 bg-slate-100 min-w-[42px]" title="Half Day">HD</th>
                                    <th className="px-2 py-2 text-right font-medium border-b border-slate-200 bg-slate-100 min-w-[42px]" title="Paid Leave">L</th>
                                    <th className="px-2 py-2 text-right font-medium border-b border-slate-200 bg-slate-100 min-w-[42px]" title="Unpaid Leave">LOP</th>
                                    <th className="px-2 py-2 text-right font-medium border-b border-slate-200 bg-slate-100 min-w-[42px]" title="Weekly Off">WO</th>
                                    <th className="px-2 py-2 text-right font-medium border-b border-slate-200 bg-slate-100 min-w-[42px]" title="Holiday">H</th>
                                    <th className="px-2 py-2 text-right font-medium border-b border-l border-slate-300 bg-emerald-50 min-w-[68px]" title="Payable Days (payroll-parity)">Payable</th>
                                    <th className="px-2 py-2 text-right font-medium border-b border-slate-200 bg-rose-50 min-w-[56px]" title="Loss of Pay (payroll-parity)">LOP days</th>
                                    <th className="px-2 py-2 text-right font-medium border-b border-l border-slate-300 bg-indigo-50 min-w-[72px]" title="Agent Tracked Hours (active + idle + locked)">Agent Hrs</th>
                                    <th className="px-2 py-2 text-right font-medium border-b border-slate-200 bg-indigo-50 min-w-[80px]" title="Attendance Net Hours (sum of net_working_minutes / 60)">Attend. Net Hrs</th>
                                    <th className="px-2 py-2 text-right font-medium border-b border-slate-200 bg-indigo-50 min-w-[80px]" title="Untracked when Agent not enrolled; Offline when enrolled but no heartbeats">Untracked/Offline</th>
                                    <th className="px-2 py-2 text-right font-medium border-b border-slate-200 bg-indigo-50 min-w-[64px]" title="Productivity %">Prod %</th>
                                </tr>
                            </thead>
                            <tbody>
                                {employees.map((emp) => {
                                    const freezeMeta = FREEZE_TONE[emp.payroll_freeze_status] || FREEZE_TONE.unfrozen;
                                    const FreezeIcon = freezeMeta.icon;
                                    const agentEnrolled = emp.workforce?.agent_enrolled;
                                    return (
                                        <tr key={emp.employee_id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`att-reg-row-${emp.employee_id}`}>
                                            <td className="sticky left-0 z-10 bg-white px-3 py-2 align-top">
                                                <div className="font-semibold text-slate-900">{emp.full_name}</div>
                                                <div className="text-[10px] text-slate-500">{emp.employee_code}{emp.designation ? ` · ${emp.designation}` : ""}</div>
                                                {emp.reporting_manager_name && (
                                                    <div className="text-[10px] text-slate-400">Mgr: {emp.reporting_manager_name}</div>
                                                )}
                                            </td>
                                            <td className="sticky left-[200px] z-10 bg-white px-3 py-2 align-top text-slate-700">{emp.department || "—"}</td>
                                            <td className="px-2 py-2 align-top">
                                                <Badge className={`${freezeMeta.tone} border text-[10px] inline-flex items-center gap-1`} data-testid={`att-reg-freeze-${emp.employee_id}`} title={emp.payroll_freeze_meta ? `${emp.payroll_freeze_meta.run_status} · run ${emp.payroll_freeze_meta.run_id}` : ""}>
                                                    <FreezeIcon size={10} weight="duotone" /> {freezeMeta.label}
                                                </Badge>
                                            </td>
                                            {emp.days.map((cell, idx) => (
                                                <td key={idx} className="p-0.5 align-top">
                                                    <button
                                                        type="button"
                                                        onClick={() => setDayDetail({ cell, employee: emp })}
                                                        data-testid={`att-reg-cell-${emp.employee_id}-${cell.date}`}
                                                        className={`w-full min-w-[26px] h-7 rounded text-[10px] font-bold border ${CODE_TONE[cell.code] || "bg-white"} hover:ring-2 hover:ring-violet-400 transition`}
                                                        title={`${cell.date} · ${cell.status || CODE_LABEL[cell.code]}${cell.is_late ? " · Late" : ""}${cell.is_early_departure ? " · Early" : ""}`}
                                                    >
                                                        {cell.code === "-" ? "" : cell.code}
                                                    </button>
                                                </td>
                                            ))}
                                            <td className="px-2 py-2 text-right border-l border-slate-200 bg-slate-50/50 font-medium">{emp.totals.P}</td>
                                            <td className="px-2 py-2 text-right bg-slate-50/50">{emp.totals.A}</td>
                                            <td className="px-2 py-2 text-right bg-slate-50/50">{emp.totals.HD}</td>
                                            <td className="px-2 py-2 text-right bg-slate-50/50">{emp.totals.L}</td>
                                            <td className="px-2 py-2 text-right bg-slate-50/50">{Number(emp.totals.LOP).toFixed(emp.totals.LOP % 1 ? 1 : 0)}</td>
                                            <td className="px-2 py-2 text-right bg-slate-50/50">{emp.totals.WO}</td>
                                            <td className="px-2 py-2 text-right bg-slate-50/50">{emp.totals.H}</td>
                                            <td className="px-2 py-2 text-right border-l border-slate-200 bg-emerald-50 font-bold text-emerald-900" data-testid={`att-reg-payable-${emp.employee_id}`}>
                                                {Number(emp.totals.payable_days).toFixed(emp.totals.payable_days % 1 ? 1 : 0)}
                                            </td>
                                            <td className="px-2 py-2 text-right bg-rose-50 font-bold text-rose-900" data-testid={`att-reg-lop-${emp.employee_id}`}>
                                                {Number(emp.totals.lop_days).toFixed(emp.totals.lop_days % 1 ? 1 : 0)}
                                            </td>
                                            <td className="px-2 py-2 text-right border-l border-slate-200 bg-indigo-50/50">{(emp.workforce?.agent_tracked_hours ?? 0).toFixed(1)}</td>
                                            <td className="px-2 py-2 text-right bg-indigo-50/50">{(emp.workforce?.attendance_net_hours ?? 0).toFixed(1)}</td>
                                            <td className="px-2 py-2 text-right bg-indigo-50/50" title={agentEnrolled ? "Offline — agent enrolled but no heartbeats during expected window" : "Untracked — no Desktop Agent enrolled"}>
                                                {agentEnrolled ? (
                                                    <span>{(emp.workforce?.offline_hours ?? 0).toFixed(1)}</span>
                                                ) : (
                                                    <span className="text-slate-400" data-testid={`att-reg-untracked-${emp.employee_id}`}>Untracked</span>
                                                )}
                                            </td>
                                            <td className="px-2 py-2 text-right bg-indigo-50/50">
                                                {emp.workforce?.productivity_pct != null ? (
                                                    <Badge className={`${pctBg(emp.workforce.productivity_pct)} text-[10px]`}>
                                                        {emp.workforce.productivity_pct.toFixed(1)}%
                                                    </Badge>
                                                ) : "—"}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </section>
            )}

            {/* Formulas + Privacy footer */}
            {data?.formulas && (
                <details className="mt-4 bg-violet-50 border border-violet-200 rounded-md p-3 text-xs" data-testid="att-reg-formulas">
                    <summary className="cursor-pointer font-medium text-violet-900 flex items-center gap-1.5">
                        <ChartBar size={12} /> How these numbers are calculated
                    </summary>
                    <dl className="mt-2 space-y-1 text-violet-800">
                        {Object.entries(data.formulas).map(([k, v]) => (
                            <div key={k}>
                                <dt className="inline font-semibold">{k}:</dt>{" "}
                                <dd className="inline">{v}</dd>
                            </div>
                        ))}
                    </dl>
                </details>
            )}

            <div className="mt-3 px-3 py-2.5 rounded-md bg-slate-50 border border-slate-200 text-[11px] text-slate-600 flex items-start gap-2" data-testid="att-reg-privacy">
                <ShieldCheck size={14} weight="duotone" className="flex-shrink-0 mt-0.5 text-slate-500" />
                <span>{data?.privacy_note}</span>
            </div>

            {/* Day-detail modal */}
            {dayDetail && <DayDetailModal cell={dayDetail.cell} employee={dayDetail.employee} onClose={() => setDayDetail(null)} />}
        </div>
    );
}
